import { getStaticConfig } from '@ms/yammer-libs-static-config';
import type { EcsPersistKey } from '@ms/yammer-libs-static-config';

import type { LegacyEcsPersistenceError } from './persistence';
import { readLegacyEcsPersistedState } from './persistence';
import { createEcsPersistenceWriteController } from './persistenceWriteController';
import type { EcsSourceSnapshot } from './store';
import { createInitialEcsSourceSnapshot, experimentsStore } from './store';

export interface ExperimentsInitializationResult {
  readonly cacheExists: boolean;
  readonly error?: LegacyEcsPersistenceError;
}

type ExperimentsInitialization =
  | {
      readonly status: 'initializing';
      readonly persistKey: EcsPersistKey | undefined;
    }
  | {
      readonly status: 'initialized';
      readonly persistKey: EcsPersistKey | undefined;
      readonly result: ExperimentsInitializationResult;
    };

let experimentsInitialization: ExperimentsInitialization | undefined;

interface ExperimentsHydration {
  readonly snapshot: EcsSourceSnapshot;
  readonly result: ExperimentsInitializationResult;
}

const createDefaultHydration = (error?: LegacyEcsPersistenceError): ExperimentsHydration => ({
  snapshot: createInitialEcsSourceSnapshot(),
  result: error === undefined ? { cacheExists: false } : { cacheExists: false, error },
});

const createExperimentsHydration = (persistKey: EcsPersistKey | undefined): ExperimentsHydration => {
  if (persistKey === undefined) {
    return createDefaultHydration();
  }

  const persistedState = readLegacyEcsPersistedState(persistKey);

  if (persistedState.status === 'success') {
    return {
      snapshot: persistedState.snapshot,
      result: { cacheExists: true },
    };
  }

  return persistedState.status === 'failed' ? createDefaultHydration(persistedState.error) : createDefaultHydration();
};

export const initializeExperiments = (requestedPersistKey?: EcsPersistKey): ExperimentsInitializationResult => {
  const persistKey = requestedPersistKey ?? getStaticConfig().ecs?.persistKey;

  if (experimentsInitialization !== undefined) {
    if (experimentsInitialization.persistKey !== persistKey) {
      throw new Error('Experiments cannot be initialized with a different ECS persistence configuration.');
    }

    if (experimentsInitialization.status === 'initializing') {
      throw new Error('Experiments initialization reentered before the persisted state was read.');
    }

    return experimentsInitialization.result;
  }

  experimentsInitialization = {
    persistKey,
    status: 'initializing',
  };

  const hydration = createExperimentsHydration(persistKey);
  experimentsInitialization = {
    persistKey,
    result: hydration.result,
    status: 'initialized',
  };
  experimentsStore.getState().applyHydratedSnapshot(hydration.snapshot);
  if (persistKey !== undefined) {
    createEcsPersistenceWriteController(persistKey);
  }

  return hydration.result;
};
